import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PageRequest } from '../../lib/api/paging';
import { listProfiles } from '../profiles/api';
import {
  activateCampaign,
  addVariant,
  createCampaign,
  deactivateCampaign,
  duplicateCampaign,
  ensureLink,
  exportPerformance,
  generateQR,
  getCampaign,
  getCampaignPerformance,
  getCampaignSettings,
  listCampaigns,
  listPerformance,
  listQRInventory,
  listSources,
  patchCampaign,
  patchVariant,
  saveQrImage,
  unarchiveCampaign,
  type Campaign,
  type CampaignFilters,
  type CampaignInput,
  type CampaignPatch,
  type DeactivateResult,
  type GenerateQRInput,
  type LibraryParams,
  getCampaignPeople,
} from './api';

export const campaignKeys = {
  all: ['campaigns'] as const,
  performance: (p: CampaignFilters & PageRequest) => [...campaignKeys.all, 'performance', p] as const,
  library: (p: LibraryParams) => [...campaignKeys.all, 'library', p] as const,
  detail: (id: string) => [...campaignKeys.all, 'detail', id] as const,
  inventory: (p: PageRequest & { campaignId?: string }) => [...campaignKeys.all, 'inventory', p] as const,
  sources: (q: string) => [...campaignKeys.all, 'sources', q] as const,
  settings: () => [...campaignKeys.all, 'settings'] as const,
  campaignPerformance: (id: string, w: { startDate?: string; endDate?: string }) =>
    [...campaignKeys.all, 'campaign-performance', id, w] as const,
  people: (id: string, p: PageRequest, existing: boolean) => [...campaignKeys.all, 'people', id, p, existing] as const,
};

/** The campaign's registered people, for the detail page's list. Inline errors, so silent. */
export function useCampaignPeople(id: string, page: PageRequest, existing = false) {
  return useQuery({
    queryKey: campaignKeys.people(id, page, existing),
    queryFn: ({ signal }) => getCampaignPeople(id, page, existing, signal),
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
}

/** Lists show their errors inline, so they are silent. */
export function usePerformance(params: CampaignFilters & PageRequest) {
  return useQuery({
    queryKey: campaignKeys.performance(params),
    queryFn: ({ signal }) => listPerformance(params, signal),
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
}

export function useCampaignLibrary(params: LibraryParams) {
  return useQuery({
    queryKey: campaignKeys.library(params),
    queryFn: ({ signal }) => listCampaigns(params, signal),
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
}

export function useQRInventory(params: PageRequest & { campaignId?: string }) {
  return useQuery({
    queryKey: campaignKeys.inventory(params),
    queryFn: ({ signal }) => listQRInventory(params, signal),
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
}

export function useCampaign(id: string) {
  return useQuery({
    queryKey: campaignKeys.detail(id),
    queryFn: ({ signal }) => getCampaign(id, signal),
    meta: { silent: true },
  });
}

/** Shown in the page's own cards, so silent. */
export function useCampaignPerformance(id: string, window: { startDate?: string; endDate?: string }) {
  return useQuery({
    queryKey: campaignKeys.campaignPerformance(id, window),
    queryFn: ({ signal }) => getCampaignPerformance(id, window, signal),
    meta: { silent: true },
  });
}

export function useCampaignSettings() {
  return useQuery({
    queryKey: campaignKeys.settings(),
    queryFn: ({ signal }) => getCampaignSettings(signal),
    staleTime: Infinity,
  });
}

export function useSourceSuggestions(q: string) {
  return useQuery({
    queryKey: campaignKeys.sources(q),
    queryFn: ({ signal }) => listSources(q, signal),
    staleTime: 60_000,
    meta: { silent: true },
  });
}

/** Connector-profile search for connection QR codes: any consumer profile by name, email or phone. */
export function useConnectorSearch(term: string) {
  return useQuery({
    queryKey: [...campaignKeys.all, 'connector', term] as const,
    queryFn: ({ signal }) => listProfiles({ searchTerm: term, page: 1, pageSize: 8 }, signal),
    enabled: term.trim().length >= 2,
    staleTime: 30_000,
    meta: { silent: true },
  });
}

/**
 * Every write refreshes every campaign read: the row, the totals, the detail.
 * `silent` writes show their error in the form (a 400 explains what to fix).
 */
function useCampaignMutation<A, R>(fn: (args: A) => Promise<R>, opts: { silent?: boolean } = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: campaignKeys.all }),
    meta: { silent: opts.silent ?? false },
  });
}

export const useCreateCampaign = () =>
  useCampaignMutation((input: CampaignInput) => createCampaign(input), { silent: true });
export const usePatchCampaign = (id: string) =>
  useCampaignMutation((body: CampaignPatch) => patchCampaign(id, body), { silent: true });
export const useDuplicateCampaign = (id: string) =>
  useCampaignMutation(({ slug, name }: { slug: string; name?: string }) => duplicateCampaign(id, slug, name), {
    silent: true,
  });
export const useDeactivateCampaign = (id: string) =>
  useCampaignMutation<void, DeactivateResult>(() => deactivateCampaign(id));
export const useActivateCampaign = (id: string) => useCampaignMutation<void, Campaign>(() => activateCampaign(id));
export const useUnarchiveCampaign = (id: string) => useCampaignMutation<void, Campaign>(() => unarchiveCampaign(id));
export const useAddVariant = (id: string) => useCampaignMutation((description: string) => addVariant(id, description));
export const usePatchVariant = (id: string) =>
  useCampaignMutation(({ value, ...body }: { value: string; description?: string; disabled?: boolean }) =>
    patchVariant(id, value, body),
  );
export const useEnsureLink = (id: string) =>
  useCampaignMutation(({ variant, lang }: { variant: string; lang: string }) => ensureLink(id, variant, lang));
/** A failed generate is shown inline (the campaign already exists), so it is silent. */
/** Saves a QR code's PNG under its file name (`saveQrImage`). */
export function useSaveQrImage() {
  return useMutation({
    mutationFn: ({ publicUrl, filename }: { publicUrl: string; filename: string }) => saveQrImage(publicUrl, filename),
  });
}

export function useGenerateQR() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GenerateQRInput) => generateQR(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: campaignKeys.all }),
    meta: { silent: true },
  });
}

export function useExportPerformance() {
  return useMutation({ mutationFn: (filters: CampaignFilters) => exportPerformance(filters) });
}
